/**
 * Optimización de imágenes en el navegador (redimensionar + recomprimir) antes
 * de incrustarlas como `data:` URL en un editor Quill.
 *
 * Contexto (opción D): las imágenes de los mensajes/comentarios de tickets se
 * guardan embebidas como base64 dentro del HTML, en lugar de subirse como
 * adjunto y referenciarse por URL protegida. Esto evita el problema de las
 * imágenes rotas/404 (un `<img src>` hacia el endpoint de descarga no lleva los
 * headers `Authorization`/`X-Tenant-Domain` que añade el `auth.interceptor`).
 * El precio es el peso del base64; por eso aquí lo reducimos al máximo.
 *
 * ⚠️ Si a futuro la carga de imágenes embebidas vuelve a ser un problema (HTML
 * demasiado pesado en BD, muchos adjuntos, imágenes grandes), hay que cambiar a
 * la "opción A": URL firmada/temporal servida por el backend que el `<img src>`
 * pueda cargar sin headers de auth. Ver `support-tickets.service.ts`
 * (`processInlineImages` / `buildAttachmentUrl`).
 */

export interface ImageOptimizeOptions {
  /** Lado máximo (ancho o alto) en px. Las imágenes más pequeñas no se amplían. */
  maxDimension?: number;
  /** Calidad de recompresión (0..1) para formatos con pérdida (webp/jpeg). */
  quality?: number;
  /** MIME de salida. WebP da el mejor ratio peso/calidad. */
  mimeType?: string;
}

export const DEFAULT_IMAGE_OPTIMIZE_OPTIONS: Required<ImageOptimizeOptions> = {
  maxDimension: 1600,
  quality: 0.7,
  mimeType: 'image/webp',
};

/**
 * Calcula las dimensiones destino conservando la proporción original y sin
 * ampliar imágenes ya más pequeñas que `maxDimension`. Función pura.
 */
export function computeTargetDimensions(
  width: number,
  height: number,
  maxDimension: number
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const largest = Math.max(width, height);
  if (largest <= maxDimension) return { width, height };
  const scale = maxDimension / largest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * Estima el tamaño en bytes del contenido de una `data:` URL en base64 a partir
 * de la longitud de su payload (4 chars base64 ≈ 3 bytes, menos el padding `=`).
 * Función pura. Devuelve 0 si no es una data URL base64 válida.
 */
export function estimateDataUrlBytes(dataUrl: string): number {
  const commaIdx = dataUrl.indexOf(',');
  if (commaIdx === -1 || !dataUrl.startsWith('data:')) return 0;
  const payload = dataUrl.slice(commaIdx + 1);
  if (!payload) return 0;
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
}

/**
 * Redimensiona y recomprime un archivo de imagen y devuelve su `data:` URL.
 * Usa APIs del navegador (`createImageBitmap`, `<canvas>`), por lo que no es una
 * función pura: la lógica testeable (dimensiones, tamaño) está extraída arriba.
 */
export async function optimizeImageToDataUrl(file: File, options: ImageOptimizeOptions = {}): Promise<string> {
  const opts = { ...DEFAULT_IMAGE_OPTIMIZE_OPTIONS, ...options };
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = computeTargetDimensions(bitmap.width, bitmap.height, opts.maxDimension);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No se pudo obtener el contexto 2D del canvas');
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, opts.mimeType, opts.quality);
    return await blobToDataUrl(blob);
  } finally {
    bitmap.close?.();
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob devolvió null'))), type, quality);
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Error al leer el blob'));
    reader.readAsDataURL(blob);
  });
}
